import type { AutomationRequestStatus } from '@adweb/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Lightbulb, MessagesSquare, Plus, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { OrganizationFilter } from '../../components/OrganizationFilter';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { PageHeader } from '../../components/ui/PageHeader';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';

import {
  type AutomationRequestRow,
  STATUS_META,
  STATUS_ORDER,
  frequencyLabel,
} from './request-labels';
import { RequestAutomationModal } from './RequestAutomationModal';
import { RequestThread, StatusPill, refreshRequestLists } from './RequestThread';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

/** Demande née d'une suggestion d'Adweb-Systèmes (« Ça m'intéresse »). */
function SuggestionTag() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-800">
      <Sparkles className="h-3 w-3" aria-hidden="true" />
      Suggestion
    </span>
  );
}

/**
 * Fiche d'une demande et fil d'échanges entre le client et Adweb-Systèmes ;
 * les rôles Adweb y changent aussi le statut (le changement s'inscrit dans le fil).
 */
function RequestDetailModal({
  request,
  canManage,
  onClose,
}: {
  request: AutomationRequestRow | null;
  canManage: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AutomationRequestStatus>('NEW');

  useEffect(() => {
    if (request) setStatus(request.status);
  }, [request]);

  const save = useMutation({
    mutationFn: () => apiClient.patch(`/automation-requests/${request?.id}`, { status }),
    onSuccess: async () => {
      await refreshRequestLists(qc);
      await qc.invalidateQueries({ queryKey: ['automation-requests', 'messages', request?.id] });
    },
  });

  return (
    <Modal
      open={!!request}
      onClose={onClose}
      title={request?.title ?? ''}
      size="lg"
      footer={
        <Button variant="secondary" type="button" onClick={onClose}>
          Fermer
        </Button>
      }
    >
      {request && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <StatusPill status={request.status} />
            <span>{request.organization.name}</span>
            <span>·</span>
            <span>{request.requestedBy?.fullName ?? 'Compte supprimé'}</span>
            <span>·</span>
            <span>{formatDate(request.createdAt)}</span>
            {request.suggestion && <SuggestionTag />}
          </div>
          <dl className="grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium text-slate-500">Fréquence</dt>
              <dd className="text-sm text-slate-900">{frequencyLabel(request.frequency)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">Outils concernés</dt>
              <dd className="text-sm text-slate-900">{request.tools ?? '—'}</dd>
            </div>
          </dl>
          <div>
            <div className="text-xs font-medium text-slate-500">Besoin</div>
            <p className="mt-1 whitespace-pre-line text-sm text-slate-800">{request.description}</p>
          </div>

          {canManage && (
            <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-4">
              <div className="min-w-[12rem] flex-1">
                <label
                  htmlFor="req-status"
                  className="mb-1 block text-xs font-medium text-slate-700"
                >
                  Statut
                </label>
                <select
                  id="req-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as AutomationRequestStatus)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  {STATUS_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_META[s].label}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => save.mutate()}
                disabled={save.isPending || status === request.status}
              >
                {save.isPending ? 'Enregistrement…' : 'Changer le statut'}
              </Button>
              <p className="w-full text-xs text-slate-500">
                Le demandeur est prévenu ; le changement apparaît dans les échanges.
              </p>
              {save.isError && (
                <p role="alert" className="w-full text-xs text-red-600">
                  L’enregistrement a échoué. Réessayez dans un instant.
                </p>
              )}
            </div>
          )}

          <div className="border-t border-slate-100 pt-4">
            <RequestThread requestId={request.id} viewerIsAdweb={canManage} />
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * Demandes d'automatisation : un client y suit ses demandes ; Adweb-Systèmes y
 * traite celles de toutes les organisations.
 */
export function RequestsPage() {
  const { user } = useAuth();
  const isPlatform = user?.role === 'SUPER_ADMIN' || user?.role === 'ADWEB_OPERATOR';
  const [params, setParams] = useSearchParams();
  const [formOpen, setFormOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<AutomationRequestStatus | ''>('');
  const [orgFilter, setOrgFilter] = useState('');

  const list = useQuery({
    queryKey: ['automation-requests', statusFilter, orgFilter],
    queryFn: async () =>
      (
        await apiClient.get<AutomationRequestRow[]>('/automation-requests', {
          params: {
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(orgFilter ? { organizationId: orgFilter } : {}),
          },
        })
      ).data,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

  // Lien direct depuis un e-mail ou une notification : /demandes?id=…
  const openId = params.get('id');
  const detail = useQuery({
    queryKey: ['automation-requests', 'detail', openId],
    queryFn: async () =>
      (await apiClient.get<AutomationRequestRow>(`/automation-requests/${openId}`)).data,
    enabled: !!openId,
    retry: false,
  });

  const rows = list.data ?? [];
  const openRequest = openId ? (rows.find((r) => r.id === openId) ?? detail.data ?? null) : null;

  function open(id: string | null) {
    setParams(id ? { id } : {}, { replace: true });
  }

  return (
    <>
      <PageHeader
        title="Demandes"
        subtitle={
          isPlatform
            ? 'Demandes d’automatisation envoyées par vos clients.'
            : 'Vos demandes d’automatisation et leur avancement.'
        }
        helpTopic={isPlatform ? 'demandes-adweb' : 'demander'}
        actions={
          <Button variant="inverse" size="sm" onClick={() => setFormOpen(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Demander une automatisation
          </Button>
        }
      />
      <RequestAutomationModal open={formOpen} onClose={() => setFormOpen(false)} />
      <RequestDetailModal request={openRequest} canManage={isPlatform} onClose={() => open(null)} />

      <section className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-4 flex flex-wrap items-center gap-2" role="group" aria-label="Filtrer">
          {isPlatform && <OrganizationFilter value={orgFilter} onChange={setOrgFilter} />}
          {(['', ...STATUS_ORDER] as const).map((s) => (
            <button
              key={s || 'all'}
              type="button"
              onClick={() => setStatusFilter(s)}
              aria-pressed={statusFilter === s}
              className={clsx(
                'rounded-full px-3 py-1.5 text-xs font-medium transition',
                statusFilter === s
                  ? 'bg-brand text-white'
                  : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50',
              )}
            >
              {s ? STATUS_META[s].label : 'Toutes'}
            </button>
          ))}
        </div>

        {list.data && rows.length === 0 ? (
          <div className="surface flex flex-col items-center gap-3 px-6 py-12 text-center">
            <Lightbulb className="h-8 w-8 text-accent" aria-hidden="true" />
            <p className="max-w-md text-sm text-slate-600">
              {statusFilter || orgFilter
                ? 'Aucune demande ne correspond à ces filtres.'
                : 'Aucune demande pour le moment. Une tâche répétitive vous prend du temps ? Décrivez-la, nous étudions comment l’automatiser.'}
            </p>
            {!statusFilter && !orgFilter && (
              <Button size="sm" onClick={() => setFormOpen(true)}>
                Demander une automatisation
              </Button>
            )}
          </div>
        ) : (
          <div className="surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap">
                <tr>
                  <th scope="col" className="px-4 py-3">
                    Demande
                  </th>
                  {isPlatform && (
                    <th scope="col" className="hidden px-4 py-3 md:table-cell">
                      Organisation
                    </th>
                  )}
                  <th scope="col" className="hidden px-4 py-3 lg:table-cell">
                    Demandé par
                  </th>
                  <th scope="col" className="hidden px-4 py-3 xl:table-cell">
                    Fréquence
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Statut
                  </th>
                  <th scope="col" className="hidden px-4 py-3 sm:table-cell">
                    Envoyée le
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  // Toute la ligne ouvre la demande ; le bouton du titre reste l'accès clavier.
                  <tr
                    key={r.id}
                    onClick={() => open(r.id)}
                    className="cursor-pointer hover:bg-slate-50"
                  >
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Le clic (souris ou clavier) remonte jusqu'à la ligne, qui ouvre la demande. */}
                        <button
                          type="button"
                          className={clsx(
                            'text-left text-slate-900 hover:text-brand hover:underline',
                            r.unread ? 'font-semibold' : 'font-medium',
                          )}
                        >
                          {r.title}
                        </button>
                        {r.unread && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-white">
                            Nouveau message
                          </span>
                        )}
                        {r.suggestion && <SuggestionTag />}
                      </div>
                      {r.lastMessage && (
                        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                          <MessagesSquare className="h-3 w-3 shrink-0" aria-hidden="true" />
                          <span className="shrink-0">{r.messageCount}</span>
                          <span className="line-clamp-1">
                            {r.lastMessage.fromAdweb === isPlatform ? 'Vous : ' : ''}
                            {r.lastMessage.statusChange && !r.lastMessage.body
                              ? `Statut : ${STATUS_META[r.lastMessage.statusChange].label}`
                              : r.lastMessage.body}
                          </span>
                        </div>
                      )}
                      {isPlatform && (
                        <div className="mt-0.5 text-xs text-slate-500 md:hidden">
                          {r.organization.name}
                        </div>
                      )}
                    </td>
                    {isPlatform && (
                      <td className="hidden px-4 py-3 text-slate-600 md:table-cell">
                        {r.organization.name}
                      </td>
                    )}
                    <td className="hidden px-4 py-3 text-slate-600 lg:table-cell">
                      {r.requestedBy?.fullName ?? 'Compte supprimé'}
                    </td>
                    <td className="hidden px-4 py-3 text-slate-600 xl:table-cell">
                      {frequencyLabel(r.frequency)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="hidden px-4 py-3 text-slate-500 sm:table-cell">
                      {formatDate(r.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
